/**
 * Onboarding Store for Atom Assist FTUX
 *
 * Session-only state (no persistence). The API (`/user/onboarding-status`)
 * is the source of truth for whether a user has completed onboarding; this
 * store tracks ephemeral UI state during an active session (progress bar,
 * current module). On page reload, SSE metadata events repopulate the state
 * if the user is still in an active onboarding dialogue.
 */

import { create } from "zustand";

export interface OnboardingProfileArtifact {
  type: "onboarding_profile";
  name: string;
  job_title: string;
  department: string;
  work_location: string;
  industry: string;
  source: string;
}

/**
 * Answer-details artifact for module 6. We intentionally do NOT carry
 * citation data here — real RAG citations live on the dialogue messages
 * and are rendered in the Sources tab, not mirrored into this pane.
 */
export interface OnboardingAnswerDetailsArtifact {
  type: "answer_details";
  question: string;
  model: string;
  profile_context: { field: string; value: string }[];
  uploaded_doc: string | null;
  source: "onboarding";
}

/** Field definition for the inline profile form rendered in module 1. */
export interface OnboardingProfileFormField {
  field: string;
  label: string;
  prompt: string;
  value: string;
}

export interface OnboardingState {
  /** Whether the user is currently in the onboarding flow */
  isOnboarding: boolean;
  /** Current module ID (e.g., "greeting", "task_declaration") */
  currentModule: string | null;
  /** Current module index (0-based) */
  currentModuleIndex: number;
  /** Total number of modules */
  totalModules: number;
  /** Dialogue ID for the onboarding chat */
  onboardingDialogueId: number | null;
  /** Profile artifact from module 1 (Greeting) */
  profileArtifact: OnboardingProfileArtifact | null;
  /** Structured answer context from module 6 (Answer + Verification) */
  answerDetails: OnboardingAnswerDetailsArtifact | null;
  /**
   * Inline profile form shown in module 1. Field definitions come from the
   * backend (via tool_response); the in-chat form and the Verification pane
   * both read & write these fields so editing stays in sync across panels.
   */
  profileFormFields: OnboardingProfileFormField[] | null;
  profileFormValues: Record<string, string>;
  /**
   * True once the profile has been saved at least once (from the chat form
   * or the Verification pane). The chat form collapses to a "saved" chip so
   * we never re-submit to the agent's input queue. The Verification pane
   * keeps its fields editable and routes subsequent saves through
   * PUT /user/profile directly — the first save unblocks the onboarding
   * flow, later saves just update the profile.
   */
  profileFormSubmitted: boolean;
  /**
   * Dialogue id of the most recently *completed* onboarding session.
   * Survives ``resetOnboarding()`` so the AtomAssistPage isOnboarding
   * sync effect can skip re-asserting ``true`` during the brief window
   * between ``onboarding_complete`` (which resets the store) and the
   * ChatStore persona slug flipping away from ``ftux-onboarding``.
   * Without this, the reset would get clobbered on the next render
   * because the derived ``isOnboardingSession`` is still true.
   */
  finalizedDialogueId: number | null;
}

export interface OnboardingActions {
  setIsOnboarding: (val: boolean) => void;
  setCurrentModule: (module: string | null) => void;
  setCurrentModuleIndex: (index: number) => void;
  setTotalModules: (total: number) => void;
  setOnboardingDialogueId: (id: number | null) => void;
  setProfileArtifact: (artifact: OnboardingProfileArtifact | null) => void;
  setAnswerDetails: (artifact: OnboardingAnswerDetailsArtifact | null) => void;
  setProfileFormFields: (fields: OnboardingProfileFormField[] | null) => void;
  setProfileFormValue: (field: string, value: string) => void;
  setProfileFormValues: (values: Record<string, string>) => void;
  setProfileFormSubmitted: (submitted: boolean) => void;
  setFinalizedDialogueId: (id: number | null) => void;
  resetOnboarding: () => void;
}

const initialState: OnboardingState = {
  isOnboarding: false,
  currentModule: null,
  currentModuleIndex: 0,
  totalModules: 7,
  onboardingDialogueId: null,
  profileArtifact: null,
  answerDetails: null,
  profileFormFields: null,
  profileFormValues: {},
  profileFormSubmitted: false,
  finalizedDialogueId: null,
};

export const useOnboardingStore = create<OnboardingState & OnboardingActions>()(
  (set) => ({
    ...initialState,

    setIsOnboarding: (val) => set({ isOnboarding: val }),
    setCurrentModule: (module) => set({ currentModule: module }),
    setCurrentModuleIndex: (index) => set({ currentModuleIndex: index }),
    setTotalModules: (total) => set({ totalModules: total }),
    setOnboardingDialogueId: (id) => set({ onboardingDialogueId: id }),
    setProfileArtifact: (artifact) => set({ profileArtifact: artifact }),
    setAnswerDetails: (artifact) => set({ answerDetails: artifact }),
    setProfileFormFields: (fields) =>
      set((state) => ({
        profileFormFields: fields,
        // Seed values from the emitted fields but preserve anything the user
        // already typed — the chat form rehydrates on reload and we don't
        // want to clobber an in-progress edit with the same emitted values.
        profileFormValues: fields
          ? fields.reduce<Record<string, string>>((acc, f) => {
              acc[f.field] = state.profileFormValues[f.field] ?? f.value ?? "";
              return acc;
            }, {})
          : {},
      })),
    setProfileFormValue: (field, value) =>
      set((state) => ({
        profileFormValues: { ...state.profileFormValues, [field]: value },
      })),
    setProfileFormValues: (values) => set({ profileFormValues: values }),
    setProfileFormSubmitted: (submitted) =>
      set({ profileFormSubmitted: submitted }),
    setFinalizedDialogueId: (id) => set({ finalizedDialogueId: id }),

    // resetOnboarding wipes the session state but preserves
    // finalizedDialogueId so the AtomAssistPage sync effect can tell
    // "this dialogue is done, don't re-assert isOnboarding" vs. a
    // never-started session.
    resetOnboarding: () =>
      set((state) => ({
        ...initialState,
        finalizedDialogueId: state.finalizedDialogueId,
      })),
  }),
);
